import { Divider } from '@mui/material';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import DoseNormalization from '~/components/Header/BreadcrumbNavigation/DoseBreadcrumb/DoseDetails/DoseProperties/DoseNormalization';
import DoseRescaler from '~/components/Header/BreadcrumbNavigation/DoseBreadcrumb/DoseDetails/DoseProperties/DoseRescaler';
import DoseProperties from '~/components/Properties/DoseProperties';
import PropertiesSidePanel from '~/components/Properties/PropertiesSidePanel';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import useDose from '~/hooks/patient/dose/useDose';
import { getDoseLabel } from '~/containers/viewer/explorer-sidebar/WorkflowSidebar/DoseStep/doseUtils';

/**
 * The panel that shows the properties of the loaded dose: a header with the
 * RTDOSE badge, the dose's name and a close button, then the dose's
 * properties, its scaling and its normalization. The panel stands above
 * `anchorEl`. It shows nothing while no dose is loaded, so unloading the
 * dose closes it.
 */
export default function DosePropertiesPanel({
    anchorEl,
    onClose,
}: {
    /** The control the panel opens from. */
    anchorEl: HTMLElement | null;
    onClose: () => void;
}) {
    const { dose, setNormalizationValue } = useDose();

    // Unloading the dose closes its panel for good: a dose loaded later
    // does not bring the panel back.
    const isShown = dose !== undefined;
    useEffect(() => {
        if (!isShown) {
            onClose();
        }
    }, [isShown, onClose]);

    return dose ? (
        <PropertiesSidePanel
            anchorEl={anchorEl}
            align="above"
            badge="RTDOSE"
            onClose={onClose}
            data-testid="dose-properties-panel"
            title={
                <FormattedMessage
                    id="datasetPicker.propertiesOf"
                    values={{
                        name: (
                            <SensitiveInfoTypography>
                                {getDoseLabel(
                                    dose.planLabel,
                                    dose.summationType
                                )}
                            </SensitiveInfoTypography>
                        ),
                    }}
                />
            }
        >
            <DoseProperties dose={dose} variant="panel" />
            <Divider />
            <DoseRescaler dose={dose} />
            <DoseNormalization
                dose={dose}
                setNormalizationValue={setNormalizationValue}
            />
        </PropertiesSidePanel>
    ) : null;
}
